import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { useAuth } from './AuthContext';
import { RefreshCw, CheckCircle, Search, ExternalLink } from 'lucide-react';

/**
 * BIDVEST RECONCILIATION LOG - Owner visibility into the auto-reconciliation job.
 * Shows every order the scheduled job confirmed against BidVest (reconciled_by_job = true),
 * with BidVest references as proof of verification.
 */

const fmt = (n) => `R ${Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleString('en-ZA', { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

const AdminReconciliationLog = () => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState('');
  const [range, setRange] = useState('7d'); // 24h | 7d | all

  const load = async () => {
    setLoading(true);
    try {
      let q = supabase
        .from('orders')
        .select('id, order_number, user_id, total, amount_due, wallet_credit_used, status, payment_status, transaction_id, gateway_reference, payment_key, reconciled_at')
        .eq('reconciled_by_job', true)
        .order('reconciled_at', { ascending: false });

      if (range === '24h') {
        q = q.gte('reconciled_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString());
      } else if (range === '7d') {
        q = q.gte('reconciled_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString());
      }

      const { data, error } = await q;
      if (error) throw error;

      const rows = data || [];
      // Fetch customer names
      const userIds = [...new Set(rows.map(o => o.user_id).filter(Boolean))];
      const names = {};
      for (let i = 0; i < userIds.length; i += 200) {
        const chunk = userIds.slice(i, i + 200);
        const { data: profs } = await supabase.from('user_profiles').select('id, name, phone').in('id', chunk);
        (profs || []).forEach(p => { names[p.id] = p; });
      }
      setOrders(rows.map(o => ({ ...o, customer: names[o.user_id] })));
    } catch (e) {
      console.error('Reconciliation log load error:', e);
      alert('Failed to load reconciliation log: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userProfile && userProfile.role === 'owner') load();
    // eslint-disable-next-line
  }, [userProfile, range]);

  const filtered = orders.filter(o => {
    const q = search.toLowerCase();
    if (!q) return true;
    return o.order_number?.toLowerCase().includes(q)
      || o.customer?.name?.toLowerCase().includes(q)
      || o.transaction_id?.toLowerCase?.().includes(q);
  });

  const totalRecovered = orders.reduce((s, o) => s + parseFloat(o.total || 0), 0);

  if (userProfile && userProfile.role !== 'owner') {
    return <div className="p-6 text-gray-600">Reconciliation Log is restricted to Owner accounts.</div>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Reconciliation Log</h1>
          <p className="text-gray-600 mt-1">Orders auto-confirmed by the BidVest reconciliation job (paid, but the webhook/redirect missed)</p>
        </div>
        <button onClick={load} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
          <RefreshCw size={18} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4 my-4">
        <div className="bg-white rounded-lg border p-4">
          <p className="text-sm text-gray-500">Orders auto-recovered ({range === '24h' ? 'last 24h' : range === '7d' ? 'last 7 days' : 'all time'})</p>
          <p className="text-2xl font-bold text-green-600">{orders.length}</p>
        </div>
        <div className="bg-white rounded-lg border p-4">
          <p className="text-sm text-gray-500">Total value recovered</p>
          <p className="text-2xl font-bold text-gray-900">{fmt(totalRecovered)}</p>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="flex items-center gap-2 flex-1 bg-white border rounded-lg px-3 py-2">
          <Search size={18} className="text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search order #, customer, or transaction ID…"
            className="flex-1 outline-none text-sm" />
        </div>
        <select value={range} onChange={e => setRange(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
          <option value="24h">Last 24 hours</option>
          <option value="7d">Last 7 days</option>
          <option value="all">All time</option>
        </select>
      </div>

      {loading ? (
        <p className="text-gray-500">Loading…</p>
      ) : (
        <div className="bg-white rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-4 py-2">Order</th>
                <th className="text-left px-4 py-2">Customer</th>
                <th className="text-right px-4 py-2">Total</th>
                <th className="text-right px-4 py-2">Wallet used</th>
                <th className="text-left px-4 py-2">Reconciled at</th>
                <th className="text-left px-4 py-2">BidVest txn</th>
                <th className="text-left px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(o => (
                <tr key={o.id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-2 font-medium text-gray-900">{o.order_number}</td>
                  <td className="px-4 py-2">
                    <div className="text-gray-900">{o.customer?.name || '—'}</div>
                    <div className="text-gray-400 text-xs">{o.customer?.phone || ''}</div>
                  </td>
                  <td className="px-4 py-2 text-right text-gray-700">{fmt(o.total)}</td>
                  <td className="px-4 py-2 text-right text-gray-500">{parseFloat(o.wallet_credit_used) > 0 ? fmt(o.wallet_credit_used) : '—'}</td>
                  <td className="px-4 py-2 text-gray-600 whitespace-nowrap">{fmtDate(o.reconciled_at)}</td>
                  <td className="px-4 py-2 text-gray-600">
                    <div className="font-mono text-xs">{o.transaction_id || '—'}</div>
                    <div className="font-mono text-xs text-gray-400">{o.gateway_reference || ''}</div>
                  </td>
                  <td className="px-4 py-2">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">
                      <CheckCircle size={12} /> {o.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <p className="p-6 text-gray-500 text-center">No auto-reconciled orders in this period</p>}
        </div>
      )}
    </div>
  );
};

export default AdminReconciliationLog;
